/**
 * AddLeadModal - Form modal to manually create a single lead
 */

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { useCreateLead } from "../hooks/useLeads";
import type { CreateLeadData } from "../Lead.types";
import { ENTITY_CONFIG } from "@/core/entityConfig";

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMPTY_FORM: CreateLeadData = {
  name: "",
  sector: "",
  city: "",
  country: "",
  address: "",
  phone: "",
  website: "",
  emailAddress: "",
  description: "",
  instagramUrl: "",
  googleUrl: "",
};

export function AddLeadModal({ isOpen, onClose }: AddLeadModalProps) {
  const [form, setForm] = useState<CreateLeadData>({ ...EMPTY_FORM });
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateLead();

  const set = (field: keyof CreateLeadData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    setError(null);

    // Strip empty strings so the backend receives undefined instead
    const payload: CreateLeadData = { name: form.name.trim() };
    for (const [key, value] of Object.entries(form)) {
      if (key !== "name" && typeof value === "string" && value.trim()) {
        (payload as unknown as Record<string, string>)[key] = value.trim();
      }
    }

    try {
      await createMutation.mutateAsync(payload);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create lead."
      );
    }
  };

  const handleClose = () => {
    setForm({ ...EMPTY_FORM });
    setError(null);
    onClose();
  };

  const inputClass =
    "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={ENTITY_CONFIG.lead.createLabel}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-lead-form"
            disabled={createMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </span>
            ) : (
              "Save Lead"
            )}
          </button>
        </>
      }
    >
      <form id="add-lead-form" onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900 mb-3">
            Basic Info
          </legend>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label htmlFor="lead-name" className={labelClass}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="lead-name"
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder="Business or contact name"
                className={inputClass}
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="lead-sector" className={labelClass}>Sector</label>
              <input
                id="lead-sector"
                type="text"
                value={form.sector ?? ""}
                onChange={set("sector")}
                placeholder="e.g. Hotel, Aesthetic Clinic, Real Estate, Wellness, Restaurant"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-city" className={labelClass}>City</label>
              <input
                id="lead-city"
                type="text"
                value={form.city ?? ""}
                onChange={set("city")}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-country" className={labelClass}>Country</label>
              <input
                id="lead-country"
                type="text"
                value={form.country ?? ""}
                onChange={set("country")}
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        {/* Contact Info */}
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900 mb-3">
            Contact
          </legend>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-email" className={labelClass}>Email</label>
              <input
                id="lead-email"
                type="email"
                value={form.emailAddress ?? ""}
                onChange={set("emailAddress")}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-phone" className={labelClass}>Phone</label>
              <input
                id="lead-phone"
                type="tel"
                value={form.phone ?? ""}
                onChange={set("phone")}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-website" className={labelClass}>Website</label>
              <input
                id="lead-website"
                type="url"
                value={form.website ?? ""}
                onChange={set("website")}
                placeholder="https://"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-address" className={labelClass}>Address</label>
              <input
                id="lead-address"
                type="text"
                value={form.address ?? ""}
                onChange={set("address")}
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        {/* Social */}
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900 mb-3">
            Social
          </legend>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-ig" className={labelClass}>Instagram URL</label>
              <input
                id="lead-ig"
                type="url"
                value={form.instagramUrl ?? ""}
                onChange={set("instagramUrl")}
                placeholder="https://instagram.com/..."
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="lead-google" className={labelClass}>Google URL</label>
              <input
                id="lead-google"
                type="url"
                value={form.googleUrl ?? ""}
                onChange={set("googleUrl")}
                placeholder="https://maps.google.com/..."
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        {/* Description */}
        <div>
          <label htmlFor="lead-desc" className={labelClass}>Description</label>
          <textarea
            id="lead-desc"
            value={form.description ?? ""}
            onChange={set("description")}
            rows={3}
            className={inputClass}
            placeholder="Notes about this lead..."
          />
        </div>
      </form>
    </Modal>
  );
}
